import React, { useMemo, useState } from 'react';
import { Copy, Check } from 'lucide-react';

/** The text a value shows as: JSON, or the preview of a value the server capped. */
export function valueText(value) {
  if (value == null) return '';
  if (typeof value === 'object' && value.__truncated) return typeof value.preview === 'string' ? value.preview : JSON.stringify(value.preview, null, 2);
  if (typeof value === 'string') return value;
  try { return JSON.stringify(value, null, 2); } catch (_) { return String(value); }
}

export const isCapped = (value) => !!value && typeof value === 'object' && value.__truncated === true;

/** One labelled, copyable, monospace block. */
export default function JsonPane({ label, value, tone }) {
  const [copied, setCopied] = useState(false);
  const text = useMemo(() => valueText(value), [value]);
  const copy = () => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) { /* clipboard denied */ }
  };
  return (
    <div className="flex min-w-0 flex-col" data-testid={`pane-${label}`}>
      <div className="mb-1 flex h-6 items-center justify-between">
        <span className="text-[11px] font-medium text-[color:var(--text-muted)]">{label}</span>
        {text && (
          <button type="button" onClick={copy} aria-label={`Copy ${label}`} title={`Copy ${label}`}
            className="inline-flex h-6 items-center gap-1 rounded px-1.5 text-[11px] text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
            {copied ? <Check size={12} aria-hidden /> : <Copy size={12} aria-hidden />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        )}
      </div>
      {text ? (
        <pre className={`m-0 max-h-[260px] min-h-[40px] overflow-auto whitespace-pre-wrap break-words rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)] p-2 font-mono text-[12px] leading-relaxed ${tone === 'danger' ? 'text-danger' : 'text-[color:var(--text-primary)]'}`}>{text}</pre>
      ) : (
        <div className="rounded-md border border-[color:var(--border-subtle)] px-2 py-2 text-[12px] text-[color:var(--text-disabled)]">None</div>
      )}
      {isCapped(value) && (
        <div className="mt-1 text-[11px] text-[color:var(--text-muted)]">Shortened: {Number(value.__bytes || 0).toLocaleString()} bytes in all.</div>
      )}
    </div>
  );
}
